<template>
  <div class="pt-4 px-4">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <div v-if="!loading">
      <!-- Page header -->
      <v-layout row align-center class="mb-4" wrap>
        <v-flex xs12 sm7>
          <div class="display-1 font-weight-bold">Route Designs</div>
          <div class="grey--text mt-1">
            Design bus routes and assign students to buses
          </div>
        </v-flex>
        <v-flex
          xs12
          sm5
          style="display:flex;align-items:center;justify-content:flex-end;gap:12px"
        >
          <v-select
            v-model="selectedSeason"
            :items="seasonData"
            item-text="season"
            item-value="season"
            label="Season"
            dense
            outlined
            hide-details
            style="max-width:220px"
            @change="onSeasonSelected"
          ></v-select>
          <v-btn color="success" @click="openCreateDialog">
            <v-icon left>mdi-plus</v-icon>
            New Route Design
          </v-btn>
        </v-flex>
      </v-layout>

      <!-- Summary cards -->
      <v-layout row class="mb-4" wrap>
        <v-flex xs6 sm3 class="pr-2 mb-3">
          <v-card class="pa-3 text-xs-center" elevation="1">
            <div class="display-1 font-weight-bold primary--text">
              {{ routeDesigns.length }}
            </div>
            <div class="caption grey--text text--darken-1">Total</div>
          </v-card>
        </v-flex>
        <v-flex xs6 sm3 class="pr-2 mb-3">
          <v-card class="pa-3 text-xs-center" elevation="1">
            <div class="display-1 font-weight-bold blue-grey--text">
              {{ countByStatus("Draft") }}
            </div>
            <div class="caption grey--text text--darken-1">Draft</div>
          </v-card>
        </v-flex>
        <v-flex xs6 sm3 class="pr-2 mb-3">
          <v-card class="pa-3 text-xs-center" elevation="1">
            <div class="display-1 font-weight-bold orange--text text--darken-2">
              {{ countByStatus("Partial") }}
            </div>
            <div class="caption grey--text text--darken-1">In Progress</div>
          </v-card>
        </v-flex>
        <v-flex xs6 sm3 class="mb-3">
          <v-card class="pa-3 text-xs-center" elevation="1">
            <div class="display-1 font-weight-bold green--text text--darken-2">
              {{ countByStatus("Complete") }}
            </div>
            <div class="caption grey--text text--darken-1">Complete</div>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Table -->
      <v-card elevation="2">
        <v-card-title>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            clearable
            style="max-width:300px"
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="routeDesigns"
          :rows-per-page-items="[15, 25, 50]"
          :pagination.sync="pagination"
          :search="search"
        >
          <template v-slot:items="props">
            <tr
              @click="openRouteDesign(props.item)"
              style="cursor:pointer;"
              class="route-row"
            >
              <td>
                <span class="font-weight-medium">{{ props.item.name }}</span>
              </td>
              <td>{{ props.item.seasonName }}</td>
              <td>{{ props.item.regID }}</td>
              <td>
                <span v-if="props.item.building">
                  <v-chip
                    v-for="b in props.item.building.split(',')"
                    :key="b"
                    small
                    class="ma-0 mr-1 mb-1"
                    color="blue lighten-4"
                    >{{ b.trim() }}</v-chip
                  >
                </span>
              </td>
              <td>
                <span v-if="props.item.branch">
                  <v-chip
                    v-for="b in props.item.branch.split(',')"
                    :key="b"
                    small
                    class="ma-0 mr-1 mb-1"
                    color="teal lighten-4"
                    >{{ b.trim() }}</v-chip
                  >
                </span>
                <span v-else class="grey--text">—</span>
              </td>
              <td>
                <span v-if="props.item.grades">
                  <v-chip
                    v-for="g in props.item.grades.split(',')"
                    :key="g"
                    small
                    class="ma-0 mr-1 mb-1"
                    color="blue-grey lighten-4"
                    >{{ g.trim() }}</v-chip
                  >
                </span>
                <span v-else class="grey--text">—</span>
              </td>
              <td>
                <v-chip
                  small
                  :color="statusColor(props.item.status)"
                  text-color="white"
                >
                  {{ props.item.status }}
                </v-chip>
              </td>
              <td>
                <span class="font-weight-medium">{{
                  props.item.completedBusCount
                }}</span>
                <span class="grey--text"> / {{ props.item.busCount }}</span>
              </td>
              <td>
                <span class="font-weight-medium">{{ props.item.totalStudentCount }}</span>
                <span v-if="props.item.poolStudentCount" class="grey--text"> / {{ props.item.poolStudentCount }}</span>
              </td>
              <td>{{ props.item.geoSchedule }}</td>
              <td>{{ props.item.createdBy }}</td>
              <td>{{ formatDate(props.item.createdAt) }}</td>
              <td @click.stop>
                <v-btn icon small @click="confirmDelete(props.item)">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="text-xs-center pa-6 grey--text">
              <v-icon large color="grey lighten-1">mdi-map-marker-path</v-icon>
              <div class="mt-2">No route designs found for this season</div>
              <v-btn color="success" class="mt-3" @click="openCreateDialog">
                Create First Route Design
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- CREATE DIALOG -->
    <v-dialog v-model="createDialog" max-width="620" persistent>
      <v-card>
        <v-card-title class="headline success white--text pa-3">
          New Route Design
        </v-card-title>
        <v-card-text class="pt-3">
          <v-layout row wrap>
            <v-flex xs12 sm8 class="pr-2">
              <v-text-field
                v-model="form.name"
                label="Route Name *"
                outlined
                dense
                ref="formName"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm4>
              <v-text-field
                v-model.number="form.busCount"
                label="Number of Buses"
                type="number"
                outlined
                dense
                min="0"
                max="50"
                hint="Auto-created on start"
                persistent-hint
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 sm6 class="pr-2">
              <v-select
                v-model="form.seasonID"
                :items="seasonData"
                item-text="season"
                item-value="id"
                label="Season *"
                outlined
                dense
                @change="onFormSeasonChange"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2">
              <v-autocomplete
                v-model="form.regID"
                :items="terms"
                label="Term (RegID)"
                outlined
                dense
                clearable
              ></v-autocomplete>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 sm6 class="pr-2">
              <v-autocomplete
                v-model="formBuildings"
                :items="buildingList"
                label="Building"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2">
              <v-autocomplete
                v-model="formBranches"
                :items="branchList"
                label="Branch"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              ></v-autocomplete>
            </v-flex>
          </v-layout>

          <v-autocomplete
            v-model="formGrades"
            :items="gradeList"
            label="Grades"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            hint="Select grades to include in this route's student pool"
            persistent-hint
          ></v-autocomplete>

          <v-layout row wrap class="mt-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-combobox
                v-model="form.geoSchedule"
                :items="getGeoScheduleOptions(form.geoSchedule)"
                label="Geo Schedule"
                outlined
                dense
                clearable
                hint="Select or type to create a new schedule name"
                persistent-hint
              ></v-combobox>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2">
              <v-text-field
                v-model="form.notes"
                label="Notes"
                outlined
                dense
              ></v-text-field>
            </v-flex>
          </v-layout>

          <div class="body-2 grey--text text--darken-1 mt-1">
            * Required. At least one of Term, Building, Branch, or Grades is
            recommended to filter the student pool.
          </div>

          <v-alert
            :value="!!formError"
            type="error"
            class="mt-3"
            @input="formError = ''"
          >
            {{ formError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="createDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="saving" @click="createRouteDesign">
            Create &amp; Open Map
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE CONFIRM DIALOG -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <div class="delete-dialog-header">
          <v-icon color="error" size="36">mdi-alert-circle-outline</v-icon>
        </div>
        <v-card-title class="delete-dialog-title"
          >Delete Route Design?</v-card-title
        >
        <v-card-text class="text-xs-center pb-1">
          <div class="font-weight-bold">
            {{ deleteTarget && deleteTarget.name }}
          </div>
          will be permanently removed, including all buses, student assignments,
          and drawings.
        </v-card-text>
        <v-card-actions class="pt-2 pb-4 px-0">
            <v-spacer></v-spacer>
            <v-btn color="default" dark @click="deleteDialog = false"
                >Cancel</v-btn
            >
            <v-btn color="error" :loading="deleting" @click="doDelete">
                <v-icon left small>mdi-delete</v-icon>Delete
            </v-btn>
            <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
export default {
  name: "RouteDesigns",
  data() {
    return {
      loading: true,
      saving: false,
      deleting: false,
      routeDesigns: [],
      seasonData: [],
      selectedSeason: "",
      terms: [],
      buildingList: [],
      branchList: [],
      gradeList: [],
      search: "",
      pagination: { rowsPerPage: 15, sortBy: "createdAt", descending: true },
      headers: [
        { text: "Name", value: "name" },
        { text: "Season", value: "seasonName" },
        { text: "Term", value: "regID" },
        { text: "Building", value: "building" },
        { text: "Branch", value: "branch" },
        { text: "Grades", value: "grades", sortable: false },
        { text: "Status", value: "status" },
        { text: "Buses", value: "busCount", sortable: false },
        { text: "Students", value: "totalStudentCount", sortable: false },
        { text: "Schedule", value: "geoSchedule" },
        { text: "Created By", value: "createdBy" },
        { text: "Created", value: "createdAt" },
        { text: "", value: "actions", sortable: false },
      ],
      createDialog: false,
      deleteDialog: false,
      deleteTarget: null,
      errorMessage: "",
      formError: "",
      geoScheduleOptions: [],
      form: {
        name: "",
        seasonID: null,
        regID: "",
        building: "",
        notes: "",
        geoSchedule: "",
        busCount: 1,
      },
      formGrades: [],
      formBranches: [],
      formBuildings: [],
    };
  },
  async created() {
    await this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      try {
        this.loading = true;
        const [seasons, terms, buildings, branches, grades] = await Promise.all(
          [
            this.$http.get("api/route/getRouteSeasons"),
            this.$http.get("api/allStudents/getRegTerms"),
            this.$http.get("api/students/getBuildingList"),
            this.$http.get("api/students/getBranchList"),
            this.$http.get("api/students/getGradeList"),
          ]
        );
        this.seasonData = seasons || [];
        this.terms = terms || [];
        this.buildingList = (buildings || []).sort();
        this.branchList = (branches || []).filter(Boolean).sort();
        this.gradeList = (grades || []).sort();

        const stored = localStorage.getItem("route-season");
        this.selectedSeason =
          stored && this.seasonData.some((s) => s.season === stored)
            ? stored
            : this.seasonData.length
            ? this.seasonData[0].season
            : "";

        await this.loadRouteDesigns();
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to load data";
      } finally {
        this.loading = false;
      }
    },
    onSeasonSelected(value) {
      this.selectedSeason = value;
      localStorage.setItem("route-season", value);
      this.loadRouteDesigns();
    },
    async loadRouteDesigns() {
      try {
        const season = encodeURIComponent(this.selectedSeason || "");
        const res = await this.$http.get("odata/routeDesign?season=" + season);
        this.routeDesigns = res && res.value ? res.value : [];
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to load route designs";
      }
    },
    countByStatus(status) {
      return this.routeDesigns.filter((r) => r.status === status).length;
    },
    statusColor(status) {
      if (status === "Complete") return "green darken-1";
      if (status === "Partial") return "orange darken-2";
      return "blue-grey";
    },
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString();
    },
    openRouteDesign(item) {
      this.$router.push({
        name: "RouteDesignMap",
        params: { id: String(item.id) },
      });
    },
    openCreateDialog() {
      this.form = {
        name: "",
        seasonID: null,
        regID: "",
        building: "",
        notes: "",
        geoSchedule: "",
        busCount: 1,
      };
      this.formGrades = [];
      this.formBranches = [];
      this.formBuildings = [];
      this.formError = "";
      const match = this.seasonData.find(
        (s) => s.season === this.selectedSeason
      );
      if (match) {
        this.form.seasonID = match.id;
        this.form.regID = match.regID || "";
        this.loadGeoSchedules(match.regID || "");
      }
      this.createDialog = true;
      this.$nextTick(() => {
        if (this.$refs.formName) this.$refs.formName.resetValidation();
      });
    },
    onFormSeasonChange(seasonId) {
      const match = this.seasonData.find((s) => s.id === seasonId);
      if (match && match.regID) {
        this.form.regID = match.regID;
        this.loadGeoSchedules(match.regID);
      }
    },
    getYearFromRegID(regID) {
      if (!regID) return "";
      const str = String(regID).trim();
      const m = str.match(/(\d{4})$/);
      return m ? m[1] : "";
    },
    getGeoScheduleOptions(selectedGeoSchedule) {
      const options = this.geoScheduleOptions.slice();
      const sel = selectedGeoSchedule ? String(selectedGeoSchedule).trim() : "";
      if (sel && options.indexOf(sel) === -1) options.unshift(sel);
      return options;
    },
    async loadGeoSchedules(regID) {
      const year = this.getYearFromRegID(regID);
      if (!year) {
        this.geoScheduleOptions = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/studentStop/GetStudentStopSchedules?year=" + year
        );
        const list = Array.isArray(response) ? response : [];
        this.geoScheduleOptions = list
          .map((s) => (s === null ? "" : String(s).trim()))
          .filter((s) => !!s)
          .filter((s, i, arr) => arr.indexOf(s) === i)
          .sort((a, b) => a.localeCompare(b));
      } catch (e) {
        this.geoScheduleOptions = [];
      }
    },
    async createRouteDesign() {
      if (!this.form.name || !this.form.name.trim()) {
        this.formError = "Route Name is required";
        return;
      }
      if (!this.form.seasonID) {
        this.formError = "Season is required";
        return;
      }
      this.saving = true;
      this.formError = "";
      try {
        const user =
          this.$store.state.authentication &&
          this.$store.state.authentication.user;
        const payload = {
          id: 0,
          name: this.form.name.trim(),
          seasonID: this.form.seasonID,
          regID: this.form.regID || null,
          building: this.formBuildings.length
            ? this.formBuildings.join(",")
            : null,
          branch: this.formBranches.length ? this.formBranches.join(",") : null,
          grades: this.formGrades.length ? this.formGrades.join(",") : null,
          geoSchedule: this.form.geoSchedule || null,
          status: "Draft",
          notes: this.form.notes || null,
          createdBy: user || "",
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };
        const res = await this.$http.post(
          "api/routeDesign/saveRouteDesign",
          payload
        );
        const newId = res && res.id;

        if (newId && this.form.busCount > 0) {
          const busPromises = [];
          for (let i = 1; i <= this.form.busCount; i++) {
            busPromises.push(
              this.$http.post("api/routeDesign/saveBus", {
                id: 0,
                routeDesignID: newId,
                busNumber: "Bus " + i,
                busID: null,
                sortOrder: i - 1,
                isCompleted: false,
                notes: null,
                createdAt: new Date().toISOString(),
              })
            );
          }
          await Promise.all(busPromises);
        }

        this.createDialog = false;
        if (newId) {
          this.$router.push({
            name: "RouteDesignMap",
            params: { id: String(newId) },
          });
        } else {
          await this.loadRouteDesigns();
        }
      } catch (e) {
        console.error(e);
        this.formError = "Failed to create route design";
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await this.$http.post(
          "api/routeDesign/deleteRouteDesign?id=" + this.deleteTarget.id
        );
        this.deleteDialog = false;
        this.deleteTarget = null;
        await this.loadRouteDesigns();
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to delete route design";
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.route-row:hover {
  background: #f5f5f5 !important;
}
.delete-dialog-header {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
}
.delete-dialog-title {
  justify-content: center !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  padding-top: 0 !important;
  padding-bottom: 4px !important;
}
</style>
